// Forecast-delete command (EPIC #157, phase P5.8 — design §2.2).
//
// The root DELETE FORECAST commit (the confirm-popup onClick) and the branch CLEAR BRANCH
// handler (`removeBranch`) moved out of AppInner into `(deps, args)` commands, plus the
// folded watchlist alert-clear (former 5.10). Two delete functions, not one: the root and
// branch side-effects genuinely diverge and are ported VERBATIM —
//   • root:   register the id for the persisted DELETE cascade, unpin it, and remove the run
//             (RUN_REMOVED clears the selection IFF it was the deleted run)
//   • branch: register + remove only — no unpin (branches aren't pinned); the root stays
//             focused, since the selection names the root, not the branch
//
// The migration-mandated seams:
//   • `dispatchRun(…)` → `deps.store.dispatch(…)`
//   • `setPinned` is threaded via the `ui` deps bag.
//   • `deletedIdsRef` (P5.8's DELETE-queue ref) → `deleteRequested(runId)` (P6): the pending-
//     delete queue moved into the run-store itself, so the commands dispatch instead of
//     mutating a threaded ref.
// NO network here: the `DELETE /api/forecasts/:id` cascade fires later off `pendingDeleteIds`
// in the persister's store subscription — these are pure store writes (no fetch, so no
// Decision-A concern).
import { runRemoved, alertSet, deleteRequested, branchArchived } from "../lib/forecast/reducer.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import { beginIntent } from "../lib/telemetry/instrument.js";
import { toast } from "../lib/ui/toast.js";
import type { CommandDeps } from "./deps.js";

// ── deps typing ───────────────────────────────────────────────────────────────
// Pragmatic types at the React boundary (decision C): the store carries real domain types;
// `setPinned` is a React handle this command doesn't own.
type AnyFn = (...args: any[]) => any;

/** Full deps for the delete ops: shared base + the React-bound `ui` bag. */
export interface DeleteDeps extends CommandDeps {
  ui: { setPinned: AnyFn };
}

/**
 * What the delete confirmation holds: one forecast, or several picked for a batch delete. A
 * pick that leaves one deletable forecast (shared rows are filtered out) is that forecast, so
 * the confirmation stays keyed by it; an empty pick is nothing to confirm.
 */
export function deleteSelection<T>(picked: T | T[] | null | undefined): T | T[] | null {
  if (!Array.isArray(picked)) return picked ?? null;
  if (picked.length === 0) return null;
  return picked.length === 1 ? picked[0] : picked;
}

/**
 * Delete a ROOT forecast (the confirm-popup commit). Registers the id so the persister's
 * subscription fires the `DELETE /api/forecasts/:id` cascade (without this the row survives
 * in Postgres and reload re-hydrates it), unpins it from the watchlist, and removes the run —
 * which also clears the selection when it was the deleted run; a background delete leaves the
 * current focus intact.
 */
export function deleteForecast(
  rawDeps: DeleteDeps,
  { runId, batch = 1 }: { runId: string; batch?: number },
): void {
  // The user's delete (P2 design §4.6). The DELETE request itself fires later from the
  // persister's queue; the store actions below carry the intent. `batch`: how many forecasts
  // the confirm deletes at once (each gets its own intent).
  const intent = beginIntent(
    "forecast.delete",
    { forecast_id: runId, batch },
    { origin: "delete-forecast.confirm" },
  );
  const deps = bindIntent(rawDeps, intent);
  deps.store.dispatch(deleteRequested(runId));
  deps.ui.setPinned((p: Record<string, unknown>) => {
    const n = { ...p };
    delete n[runId];
    return n;
  });
  deps.store.dispatch(runRemoved(runId));
  intent.end("ok");
}

/**
 * Delete a BRANCH (CLEAR BRANCH). Lane-aware: branch ids are globally unique, so this removes
 * it from whichever root owns it. Registers the id for the persisted DELETE cascade (else
 * "deleted branches come back" on reload) and removes the run. Unlike the root delete it does
 * NOT unpin (branches aren't pinned); CLEAR BRANCH leaves the root focused.
 */
export function deleteBranch(deps: DeleteDeps, { branchId }: { branchId: string }): void {
  // WHICH DELETE, decided BEFORE the row leaves the store — afterwards there is
  // nothing left to ask. A branch of a forecast shared WITH the viewer is not
  // theirs to delete by the owner route; it goes to `DELETE /api/forecasts/:id/branch`,
  // authorized against the branch's PARENT the way branch creation is.
  //
  // Owner leg checked FIRST, the tie-break `sliceOf` makes in the reducer: the
  // route and the slice a row is removed from must not be able to disagree.
  const state = deps.store.getState();
  const sharedBranch = state.runsById?.[branchId] == null ? state.sharedRunsById?.[branchId] : null;
  if (sharedBranch) {
    // TWO ROUTES, TWO ID SPACES: the owner route takes a `client_id` (which IS
    // the runId, so the queue below passes one straight through); this one takes
    // the pg UUID, since a client_id is unique only within ONE user. No
    // forecastId means the branch was never persisted — nothing to delete.
    if (sharedBranch.forecastId) {
      // Fired here rather than through `pendingDeleteIds`: that queue means one
      // thing, the owner cascade. Best-effort and un-retried, exactly like it.
      void deps.services.api.deleteSharedBranch(sharedBranch.forecastId);
    }
  } else {
    deps.store.dispatch(deleteRequested(branchId));
  }
  deps.store.dispatch(runRemoved(branchId));
}

/**
 * CLEAR BRANCH — ARCHIVE the branch, don't delete it. Its row survives with a `clearedAt`
 * stamp: the live lane drops it (and its own branches) at once, while a past mark from before
 * the clear still draws it (`selectVisibleBranchRuns`). The server also untracks it. With
 * persistence disabled (503) the local archive simply stands.
 *
 * Optimistic: the stamp lands in the store before the request, and the server's own stamp
 * replaces it on success. A refusal puts the branch back rather than leaving it hidden
 * locally and live on reload. A branch the server has no row for (404: never persisted, or a
 * collaborator's branch with no UUID yet) has nothing to keep for history, so it takes the
 * old delete path — which also covers a save that lands after this click.
 */
export async function archiveBranch(
  rawDeps: DeleteDeps,
  { branchId }: { branchId: string },
): Promise<void> {
  const state = rawDeps.store.getState();
  const own = state.runsById?.[branchId] ?? null;
  const shared = own == null ? (state.sharedRunsById?.[branchId] ?? null) : null;
  if (!own && !shared) return;
  const intent = beginIntent(
    "branch.clear",
    { forecast_id: branchId, kind: "archive" },
    { origin: "timeline.branch-clear", surface: "timeline.branch" },
  );
  const deps = bindIntent(rawDeps, intent);
  const sharedUuid = shared?.forecastId ?? null;
  if (shared && !sharedUuid) {
    deleteBranch(deps, { branchId });
    intent.end("ok");
    return;
  }
  deps.store.dispatch(branchArchived(branchId, new Date().toISOString()));
  let res;
  try {
    res = sharedUuid
      ? await deps.services.api.archiveBranch(sharedUuid, { shared: true })
      : await deps.services.api.archiveBranch(branchId);
  } catch (e) {
    intent.end("error");
    toast("Couldn't clear this scenario. Check your connection and try again.", { kind: "error" });
    throw e;
  }
  if (res.ok) {
    if (res.clearedAt) deps.store.dispatch(branchArchived(branchId, res.clearedAt));
    intent.end("ok");
    return;
  }
  // No persistence at all (a local stack without Postgres): there is no server copy to
  // disagree with, so the local archive stands for the session.
  if (res.status === 503) {
    intent.end("ok");
    return;
  }
  if (res.status === 404) {
    deps.store.dispatch(branchArchived(branchId, null));
    deleteBranch(deps, { branchId });
    intent.end("ok");
    return;
  }
  console.warn("[branch] archive rejected", res.status);
  deps.store.dispatch(branchArchived(branchId, null));
  toast("Couldn't clear this scenario. Try again.", { kind: "error" });
  intent.end("error");
}

/**
 * Clear a watchlisted forecast's alert flag (Forecast Tracking — #49). A thin store write;
 * the call sites (row-select / open-settings) keep their own gating (`forecastTracking`/
 * `f.alert`), so this is unconditional by design.
 */
export function clearForecastAlert(deps: DeleteDeps, { runId }: { runId: string }): void {
  deps.store.dispatch(alertSet(runId, false));
}
